/**
 * User Service
 * 
 * Data layer service for user operations.
 * 
 * Singleton pattern for consistent state and efficient resource usage.
 * Matches the architecture of CloudStorageService and other service singletons.
 * 
 * Architecture:
 * UseCase → UserService → Firestore
 */

import { doc, setDoc } from 'firebase/firestore';
import type { PageCursor } from '@/data/types/json';
import { db, auth } from '@/data/services/firebase';
import { appLogger } from '@/domain/services/analytics/appLogger';
import { firestoreService } from '@/data/services/firestoreService';
import { cloudFunctionsService } from '@/data/services/cloudFunctionsService';
import { cloudStorageService } from '@/data/storage/remote/cloudStorageService';
import { UserRemapper } from '@/domain/remappers/user/userRemapper';
import type { UserEntity } from '@/domain/entities/user/userEntity';
import type { OnboardInfoDto } from '@/data/dtos/user/onboardInfoDto';
import { shouldFilterOutBlockedUserUseCase } from '@/domain/usecases/user/shouldFilterOutBlockedUserUseCase';
import { SERVER_FETCH_CONSTANTS } from '@/domain/constants/serverFetchConstants';
import { LastUsersOrderBy, getLastUsersOrderByField } from '@/domain/enums/user/lastUsersOrderBy';

export class UserService {
  private static instance: UserService;

  private constructor() { }

  public static getInstance(): UserService {
    if (!UserService.instance) {
      UserService.instance = new UserService();
    }
    return UserService.instance;
  }

  /**
   * Fetch user details by Firebase ID. Returns null when the document does
   * not exist (mirrors Flutter's `UsersRepository.getUserById` returning
   * `Future<User?>`); Firestore transport errors still throw.
   *
   * @param userId - Firebase user ID
   * @returns UserEntity with user data, or null if not found
   */
  async fetchUserDetails(userId: string): Promise<UserEntity | null> {
    const userData = await firestoreService.getUser(userId);
    if (!userData) {
      return null;
    }
    return UserRemapper.toEntity(userData);
  }

  /**
   * Fetch multiple user documents by their Firebase IDs and apply the
   * canonical blocked-user filter against the caller-supplied current user.
   *
   * Mirrors Flutter's `UsersRepository.getSavedUsers` end-to-end
   * (`lib/data/data_sources/users_data_source.dart:225-251`):
   *   collectionGroup('users') where firebase_id whereIn valuesToSearch
   *   orderBy name desc, then post-filter blocked rows.
   *
   * Flutter's check is one-sided (`target.blockedUsers.contains(me)`); the
   * web canonical predicate `shouldFilterOutBlockedUserUseCase` is two-sided
   * (`me.blockedUsers` AND `me.usersBlockedMe`). The two-sided form is
   * strictly stronger and is what every other web user-list flow uses
   * (subscriptions, search, etc.) so we deliberately reuse it here instead
   * of replicating the Flutter expression literally.
   *
   * Chunking contract: Firestore's `in` operator accepts at most
   * `SERVER_FETCH_CONSTANTS.FIRESTORE_WHERE_IN_MAX_VALUES` (= 10) values.
   * Callers MUST chunk to that cap upstream — this service issues a single
   * `firestoreService.getUsersByFirebaseIds` call and does NOT chunk
   * internally. Oversized inputs are rejected by Firestore itself; the
   * resulting error is caught and swallowed (see below).
   *
   * Errors are logged and swallowed (returning `[]`) to match Flutter's
   * `print` + empty response on catch.
   *
   * @param firebaseIds - Firebase IDs to look up (max
   *                      `FIRESTORE_WHERE_IN_MAX_VALUES` per call)
   * @param currentUser - Redux-supplied current user (for blocked-user filter)
   * @returns Remapped `UserEntity[]` with blocked rows dropped
   */
  async fetchUsersByIds(
    firebaseIds: string[],
    currentUser: UserEntity | null,
  ): Promise<UserEntity[]> {
    if (firebaseIds.length === 0) {
      return [];
    }

    try {
      const rawUsers = await firestoreService.getUsersByFirebaseIds(firebaseIds);
      const users = rawUsers.map(data => UserRemapper.toEntity(data));

      return users.filter(
        user => !shouldFilterOutBlockedUserUseCase.execute(user.firebaseId, currentUser),
      );
    } catch (error) {
      appLogger.error('Error fetching users by ids', 'UserService.fetchUsersByIds', { error: error as Error });
      return [];
    }
  }

  /**
   * Save user profile image to Firestore
   * Matches Flutter's saveUserProfileImage from users_data_source.dart
   * 
   * @param imageUrl - Profile image URL/path to save
   */
  async saveUserProfileImage(imageUrl: string | null): Promise<void> {
    try {
      const currentUser = auth.currentUser;

      if (!currentUser) {
        appLogger.error('No authenticated user for saving profile image', 'UserService.saveUserProfileImage');
        return;
      }

      const userRef = doc(db, 'users', currentUser.uid);

      // Merge update to only modify profile_image field
      await setDoc(userRef, {
        profile_image: imageUrl
      }, { merge: true });

      console.log('[UserService] Profile image saved to Firestore:', imageUrl);
    } catch (error) {
      appLogger.error('Error saving profile image', 'UserService.saveUserProfileImage', { error: error as Error });
      throw error;
    }
  }

  /**
   * Save or remove a user from current user's favorites
   * Matches Flutter's UsersRepository.saveUser pattern
   *
   * @param userId - Firebase ID of the user to save/remove
   * @param shouldSave - true to save, false to remove
   * @returns Promise<boolean> - Success status
   */
  async saveUserToFavorites(userId: string, shouldSave: boolean): Promise<boolean> {
    try {
      const currentUser = auth.currentUser;

      if (!currentUser) {
        appLogger.error('No authenticated user for saving to favorites', 'UserService.saveUserToFavorites');
        return false;
      }

      const success = await firestoreService.saveUserToFavorites(
        currentUser.uid,
        userId,
        shouldSave,
        Date.now()
      );

      if (success) {
        console.log(`[UserService] User ${shouldSave ? 'saved to' : 'removed from'} favorites:`, userId);
      }

      return success;
    } catch (error) {
      appLogger.error('Error saving user to favorites', 'UserService.saveUserToFavorites', { error: error as Error });
      return false;
    }
  }

  /**
   * Block or unblock a user
   * Calls cloud function to handle blocking logic
   *
   * @param userId - Firebase ID of the user to block/unblock
   * @param groupChatId - Group chat ID for the message node
   * @returns Promise<boolean> - Success status
   */
  async blockUser(userId: string, groupChatId: string): Promise<boolean> {
    try {
      const currentUser = auth.currentUser;

      if (!currentUser) {
        appLogger.error('No authenticated user for blocking', 'UserService.blockUser');
        return false;
      }

      const success = await cloudFunctionsService.blockUser(userId, groupChatId);

      if (success) {
        console.log(`[UserService] Block/unblock user successful:`, userId);
      }

      return success;
    } catch (error) {
      appLogger.error('Error blocking user', 'UserService.blockUser', { error: error as Error });
      return false;
    }
  }

  /**
   * Search users by name prefix
   * Matches Flutter's UsersRepository.searchUsers
   *
   * @param keyword - Search keyword
   * @param cursor - Pagination cursor
   * @param limitCount - Results per page
   * @param searchLastName - If true, search lastFirstNameLower field
   * @returns Paginated user search results
   */
  async searchUsers(
    keyword: string,
    cursor?: PageCursor,
    limitCount: number = 6,
    searchLastName: boolean = false
  ): Promise<{
    users: UserEntity[];
    hasMore: boolean;
    cursor?: PageCursor;
  }> {
    const result = await firestoreService.searchUsers(
      keyword,
      cursor,
      limitCount,
      searchLastName
    );

    const currentUser = auth.currentUser;
    let users = result.items.map(item => UserRemapper.toEntity(item));

    // Filter out users who have blocked the current user
    if (currentUser) {
      users = users.filter(
        user => !user.blockedUsers || !user.blockedUsers.includes(currentUser.uid)
      );
    }

    return {
      users,
      hasMore: result.hasMore,
      cursor: result.cursor,
    };
  }

  /**
   * Fetch the latest users, paginated, for the admin Latest Users list.
   *
   * Mirrors Flutter's `UsersRepository.getUsers`
   * (`lib/data/repository/users_repository.dart:205-215`) → `UsersDataSource.getUsers`:
   * resolves the chosen sort option to its Firestore field via
   * `getLastUsersOrderByField`, issues the cursor-paginated query through
   * `firestoreService.getLatestUsers`, and remaps each raw document into a
   * `UserEntity` (via `UserRemapper.toEntity`, never an `as UserDto` cast).
   *
   * No blocked-user filtering is applied here: Flutter's
   * `latest_users_screen.dart` (`:85-92`) `addAllUnique`s the returned users
   * with no filter — this is an admin "see all latest accounts" view, so
   * filtering would diverge from parity.
   *
   * @param orderBy - Sort option (created vs last-seen timestamp)
   * @param cursor - Pagination cursor (undefined for the first page)
   * @param limitCount - Page size (defaults to DEFAULT_LIMIT = 9)
   * @returns Remapped users plus pagination state
   */
  async fetchLatestUsers(
    orderBy: LastUsersOrderBy,
    cursor?: PageCursor,
    limitCount: number = SERVER_FETCH_CONSTANTS.DEFAULT_LIMIT,
  ): Promise<{
    users: UserEntity[];
    hasMore: boolean;
    cursor?: PageCursor;
  }> {
    const orderByField = getLastUsersOrderByField(orderBy);

    const result = await firestoreService.getLatestUsers(
      orderByField,
      limitCount,
      cursor,
    );

    const users = result.items.map(item => UserRemapper.toEntity(item));

    return {
      users,
      hasMore: result.hasMore,
      cursor: result.cursor,
    };
  }

  /**
   * Edit user profile by calling onboardUser cloud function
   * Reuses onboarding logic for profile updates
   *
   * @param onboardInfo - OnboardInfoDto containing updated profile info
   * @returns Promise<boolean> - Success status
   */
  async editUserProfile(onboardInfo: OnboardInfoDto): Promise<boolean> {
    return cloudFunctionsService.onboardUser(onboardInfo);
  }

  /**
   * Upload a picked profile image to the user profiles bucket.
   *
   * Domain seam over the data-layer proxy, so callers obey the
   * UseCase → Service → Data flow. Fronts the call Flutter makes at
   * `UsersRepository.saveUserDetails` (lib/data/repository/users_repository.dart:71)
   * → `AppStorageService.uploadUserProfileImage`
   * (lib/services/app_storage_service.dart:371-403). The *decision* to upload
   * (Flutter's `changedProfileImage`, `:65-67`) belongs to the caller, not here.
   *
   * Contract: resolves the persisted short string `expause-profiles%2F{uid}` on
   * success, or **`null` on any failure** — bad/undecodable image, CORS preflight
   * rejection, unauthenticated, or storage error — because the proxy swallows and
   * logs rather than throwing (cloudStorageService.ts:114-117). A `null` means
   * "upload failed", NOT "no change". `EditUserProfileUseCase` deliberately aborts
   * the save rather than persisting it (Flutter does persist it,
   * `users_repository.dart:68-71`, wiping the avatar through the `onboardUser`
   * callable's `merge` set; see that use case for why the web diverges).
   *
   * @param profileImage - Local (`blob:`) or remote (`http`) image URL to upload
   * @returns Short storage path (`expause-profiles%2F{uid}`), or null on failure
   */
  async uploadUserProfileImage(profileImage: string): Promise<string | null> {
    return cloudStorageService.uploadUserProfileImage(profileImage);
  }
}

// Export singleton instance for convenience
export const userService = UserService.getInstance();
